import { Children, isValidElement, lazy, Suspense, useEffect, useState, type ComponentType, type LazyExoticComponent, type ReactNode } from 'react';
import { createRoutesFromChildren, matchRoutes } from 'react-router';
import { Loading } from '../components/ui/Loading.js';
import { markChunkError } from './chunkError.js';

/**
 * ── כל מסך בקובץ משלו (02/11/2026) ─────────────────────────────────────────
 *
 * המנכ״ל: „לפעמים זמן העלאת האפליקציה בטלפון ארוך." נמדד: כל הלקוח היה קובץ
 * אחד — 2.3MB בדחיסה — וטלפון שפתח את מסך הכניסה, או עמדה ברצפה, הוריד גם את
 * הגאנט, הקטלוג והתלת־ממד. כאן כל מסך נטען רק כשנכנסים אליו.
 *
 * המסכים מייצאים בשם (`export function XPage`), ולכן העטיפה בוחרת את השם.
 */
export function lazyNamed<M extends Record<string, unknown>, K extends keyof M>(
  load: () => Promise<M>,
  name: K,
): LazyExoticComponent<M[K] extends ComponentType<infer P> ? ComponentType<P> : never> {
  const C = lazy(async () => {
    try {
      const m = await load();
      return { default: m[name] as never };
    } catch (err) {
      // ⚠ אחרי עדכון גרסה הקבצים הישנים נמחקים מהשרת, ולשונית שנפתחה לפני
      // העדכון מבקשת קובץ שכבר אינו קיים. מסמנים את השגיאה, ו-ErrorBoundary
      // אומר „גרסה חדשה — טען מחדש" במקום מסך לבן או „המסך נתקע".
      throw markChunkError(err);
    }
  });
  // טעינה מוקדמת: הדפדפן מאחד import() לאותו קובץ, כך שזה לא מוריד פעמיים.
  (C as unknown as { preload: () => void }).preload = () => { load().catch(() => undefined); };
  return C as never;
}

/**
 * ── בלי מפל: המעטפת והמסך יוצאים יחד עם בדיקת הכניסה ─────────────────────
 *
 * בלי זה הסדר היה טור: הקובץ הראשי ← „מי מחובר" ← קובץ המעטפת ← קובץ המסך ←
 * נתוני המסך. כאן, ברגע שהקובץ הראשי רץ, מבקשים את כל קבצי הנתיב של הכתובת
 * הנוכחית — במקביל לבדיקת הכניסה.
 */
export function preloadMatched(routes: ReactNode, pathname: string): void {
  try {
    const matches = matchRoutes(createRoutesFromChildren(routes), pathname) ?? [];
    for (const m of matches) preloadElement(m.route.element);
  } catch { /* טעינה מוקדמת היא רשות — המסך ייטען בכל מקרה כשיצויר */ }
}

function preloadElement(node: ReactNode): void {
  if (!isValidElement(node)) return;
  const pre = (node.type as { preload?: () => void }).preload;
  if (typeof pre === 'function') pre();
  Children.forEach((node.props as { children?: ReactNode }).children, preloadElement);
}

/**
 * „טוען…" רגוע: מופיע רק אחרי 250ms. מסך שנטען מהר לא מהבהב ספינר, ומסך
 * שנטען לאט אומר שהוא בדרך.
 */
export function RouteLoading(): JSX.Element {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const id = setTimeout(() => setShow(true), 250);
    return () => clearTimeout(id);
  }, []);
  return (
    <div data-route-loading="" className="min-h-[40vh] flex items-center justify-center">
      {show && <Loading variant="block" />}
    </div>
  );
}

export function RouteSuspense({ children }: { children: ReactNode }): JSX.Element {
  return <Suspense fallback={<RouteLoading />}>{children}</Suspense>;
}
